// @ts-nocheck
import React, { useMemo } from 'react';
import { ComboboxFK } from '@/components/shared/ComboboxFK';
import { useMaterialLookup } from '@/hooks/useFKLookup';
import MaterialFormModal from '@/pages/master/materials/MaterialFormModal';

interface MaterialPickerComboboxProps {
    value: string;
    onChange: (id: string) => void;
    label?: string;
    placeholder?: string;
    required?: boolean;
    error?: boolean;
    disabled?: boolean;
    className?: string;
    hideLabel?: boolean;
    onNameChange?: (name: string) => void;
    /** Called with the full material object when selected, useful for autofill */
    onMaterialSelect?: (material: any) => void;
    /** Filter materials by GSM exactly */
    gsm?: string | number;
    /** Filter materials by type */
    typeId?: string;
    /** Filter materials by category (e.g. PAPER) */
    category?: 'PAPER' | 'INK' | 'GLUE' | 'FILM' | 'CHEMICAL' | 'PLATE' | 'OTHER';
    /** Fallback display for auto-generated or custom materials not yet in DB */
    fallbackCode?: string;
    fallbackName?: string;
    fallbackWidth?: number | string;
    fallbackLength?: number | string;
}

export function MaterialPickerCombobox({
    gsm,
    typeId,
    category,
    onMaterialSelect,
    fallbackCode,
    fallbackName,
    fallbackWidth,
    fallbackLength,
    ...props
}: MaterialPickerComboboxProps) {
    // Fetch larger limit to ensure we get matching materials if filtering client-side
    const { options, isLoading } = useMaterialLookup(true, '', 500);

    const filteredOptions = useMemo(() => {
        const list = options.filter((opt) => {
            const material = opt.original;
            if (!material) return true;
            
            if (gsm && String(material.gsm) !== String(gsm)) {
                return false;
            }
            if (typeId && material.typeId !== typeId) {
                return false;
            }
            if (category && material.category && material.category !== category) {
                return false;
            }
            return true;
        });

        // If value is an auto-generated or custom material not yet in options, inject a virtual option so ComboboxFK displays it cleanly
        if (props.value && !list.some((o) => o.id === props.value || o.code === props.value)) {
            const code = fallbackCode || props.value;
            const name = fallbackName || code;
            list.unshift({
                id: props.value,
                code,
                name: name.includes(code) ? name : `${code} - ${name}`,
                subText: 'Mã tự sinh theo khổ in (Tự động lưu danh mục khi Lưu LSX)',
                original: {
                    id: props.value,
                    materialId: code,
                    code,
                    name,
                    category: category || 'PAPER',
                    gsm: gsm || '',
                    width: fallbackWidth,
                    length: fallbackLength,
                    isAutoGenerated: true,
                },
            });
        }

        return list;
    }, [options, gsm, typeId, category, props.value, fallbackCode, fallbackName, fallbackWidth, fallbackLength]);

    const handleChange = (id: string) => {
        props.onChange(id);
        if (onMaterialSelect) {
            const selectedOpt = filteredOptions.find((o) => o.id === id);
            if (selectedOpt && selectedOpt.original) {
                onMaterialSelect(selectedOpt.original);
            } else {
                onMaterialSelect(null);
            }
        }
    };

    return (
        <ComboboxFK
            {...props}
            onChange={(id, opt) => {
                handleChange(id);
                if (onMaterialSelect && opt?.original) {
                    onMaterialSelect(opt.original);
                }
            }}
            options={filteredOptions}
            loading={isLoading}
            quickAddLabel="Thêm vật tư mới"
            addFormContent={
                <MaterialFormModal
                    initialData={{
                        materialId: fallbackCode || undefined,
                        name: fallbackName || undefined,
                        typeId: typeId || undefined,
                        category: category || undefined,
                        gsm: gsm ? String(gsm) : undefined,
                        width: fallbackWidth ? Number(fallbackWidth) : undefined,
                        length: fallbackLength ? Number(fallbackLength) : undefined,
                    }}
                />
            }
            addModalSize="xl"
        />
    );
}
